import React, { useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { PageTitle, Loading, Checkbox, Input, Button } from '../root/components/common'
import { Redirect, withRouter } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import * as actions from './actions'
import { Table } from '../root/components/common/table'
import { createSelector } from 'reselect'
import { getRoleTableData } from './selectors'
import { DEFAULT_ROLES, SIDEBAR_MENU_NAMES, SYSTEM_COMPONENTS_IDS } from '../root/constants'
import { useState } from 'react'
import { getNewRole, getRoleName } from '../../utils'
import { enqueueSnackbar } from '../root/actions'
import { UserPermissions } from '../../services/permissions'

const getRoleTableDataSelector = createSelector(
    state => state.userManagement.role,
    state => state.main.user,
    (role, user) => getRoleTableData(role, user)
)

const useStyles = makeStyles(theme => ({
    wrapper: {
        height: 'calc(100% - 70px)'
    },
    link: {
        color: theme.textPrimary,
        textDecoration: 'none',
        fontWeight: 'bold',
        cursor: 'pointer'
    },
    nameInput: {
        width: 340,
        marginBottom: 20
    },
    checkboxFix: {
        marginLeft: '0 !important'
    },
    pageTitle: {
        justifyContent: 'space-between'
    }
}))

function Role(props) {
    const roleId = props.match.params.roleId
    const isNew = props.match.params.roleId === 'new'
    const classes = useStyles()
    const dispatch = useDispatch()
    const { role, newRoleId } = useSelector(state => state.userManagement)
    const { isLoading, locale, p, user } = useSelector(state => state.main)
    const roleTableData = useSelector(getRoleTableDataSelector)

    const columns = [
        {
            key: 'component',
            label: locale.userManagement.component,
            sortable: false,
            dataType: 'string',
            useCustomRenderer: true
        },
        {
            key: 'create',
            label: locale.userManagement.create,
            sortable: false,
            dataType: 'boolean',
            useCustomRenderer: true
        },
        {
            key: 'read',
            label: locale.userManagement.read,
            sortable: false,
            dataType: 'boolean',
            useCustomRenderer: true
        },
        {
            key: 'update',
            label: locale.userManagement.update,
            sortable: false,
            dataType: 'boolean',
            useCustomRenderer: true
        },
        {
            key: 'delete',
            label: locale.userManagement.delete,
            sortable: false,
            dataType: 'boolean',
            useCustomRenderer: true
        }
    ]

    // must be placed here
    const isEditable = (role) => {
        return !DEFAULT_ROLES.hasOwnProperty(role.code)
    }

    const [name, setName] = useState(role ? getRoleName(role, locale) : '')
    const [components, setComponents] = useState(role ? role.components : null)

    const onPermissionToggle = (value, componentName, permission) => {
        const newComponents = { ...components }
        newComponents[componentName][permission] = value
        setComponents(newComponents)
    }

    const tableCustomRenderer = ({ rowData, cellData, columnKey }) => {
        const isNotUsedPermission = (rowData.component === SYSTEM_COMPONENTS_IDS.devices
            && (columnKey === 'create' || columnKey === 'delete'))
            || (rowData.component === SYSTEM_COMPONENTS_IDS.reports
            && columnKey === 'delete')
        switch (columnKey) {
            case 'component':
                return (
                    <span>
                        {SIDEBAR_MENU_NAMES(locale)[cellData]}
                    </span>
                )
            default:
                return (
                    isNotUsedPermission
                        ? null
                        : <Checkbox
                                className={classes.checkboxFix}
                                onClick={onPermissionToggle.bind(
                                    this,
                                    !components[rowData.component][columnKey],
                                    rowData.component,
                                    columnKey
                                )}
                                disabled={!isEditable(role)/*  || !isGranted() pass customer_id -> get customer id fix */}
                                checked={components[rowData.component][columnKey]}
                            />
                )
        }
    }

    const isValid = (data) => {
        let ok = true
        if (data.name.length < 3) {
            ok = false
            dispatch(enqueueSnackbar({
                message: locale.userManagement.nameIsTooShort,
                options: {
                    variant: 'warning'
                }
            }))
        }
        return ok
    }

    const save = (isNew, body, id) => {
        if (isValid) {
            if (!isNew) {
                dispatch(actions.roleUpdate(id, body))
            } else {
                dispatch(actions.roleCreate(body))
            }
        }
    }

    useEffect(() => {
        return () => {
            dispatch(actions.roleCreateSuccess(null))
            dispatch(actions.roleRequestSuccess(null))
        }
    }, [])

    useEffect(() => {
        if (!isNew) {
            dispatch(actions.roleRequest(roleId))
        } else {
            dispatch(actions.roleRequestSuccess(getNewRole(user.original_customer_id)))
        }
    }, [roleId])

    useEffect(() => {
        setName(role ? getRoleName(role, locale) : '')
        setComponents(role ? role.components : null)
    }, [role])

    return (
        <div className={classes.wrapper}>
            {isLoading &&
                <Loading />
            }
            {!isLoading && !role && !roleTableData &&
                <div>
                    {locale.userManagement.roleNotFound}
                </div>
            }
            {!isLoading && role && roleTableData && components &&
                <React.Fragment>
                    <PageTitle
                        title={getRoleName(role, locale)}
                        className={classes.pageTitle}
                    >
                        {(isNew ? UserPermissions.user().create : (UserPermissions.user().update && isEditable(role))) &&
                            <Button
                                onClick={save.bind(
                                    this,
                                    isNew,
                                    {
                                        name,
                                        customer_id: user.original_customer_id,
                                        components
                                    },
                                    roleId
                                )}
                            >
                                {locale.common.save}
                            </Button>
                        }
                    </PageTitle>
                    <div>
                        <Input
                            className={classes.nameInput}
                            label={locale.common.name}
                            type='text'
                            setValue={setName}
                            value={name}
                            disabled={!isEditable(role)}
                        />
                    </div>
                    <Table
                        rows={roleTableData}
                        columns={columns}
                        customRenderer={tableCustomRenderer}
                    />
                </React.Fragment>
            }
            {newRoleId &&
                <Redirect to={{ pathname: `/user-management/roles/${newRoleId}` }} />
            }
        </div>
    )
}

export default withRouter(Role)
